// Built-in quick-selection presets. Every preset is computed lazily against the
// active timezone so "Last 7 Days" always means the last 7 days in *that* zone,
// not the browser's. Callers can pass their own list to fully override these.

import type { DatePickerPreset, DatePickerMode } from './types';
import {
  addDays,
  addMonths,
  civil,
  civilFromInstant,
  daysInMonth,
  instantFromCivil,
  quarterStartMonth,
  startOfWeek,
} from './date-utils';

/** Instant at 00:00:00 of a civil day in `tz`. */
function dayStart(y: number, m: number, d: number, tz: string): Date {
  return instantFromCivil(civil(y, m, d, { hour: 0, minute: 0, second: 0 }), tz);
}
/** Instant at 23:59:59 of a civil day in `tz`. */
function dayEnd(y: number, m: number, d: number, tz: string): Date {
  return instantFromCivil(civil(y, m, d, { hour: 23, minute: 59, second: 59 }), tz);
}

const single: DatePickerPreset[] = [
  {
    label: 'Today',
    getValue: ({ timezone, now }) => {
      const t = civilFromInstant(now, timezone);
      return dayStart(t.year, t.month, t.day, timezone);
    },
  },
  {
    label: 'Yesterday',
    getValue: ({ timezone, now }) => {
      const y = addDays(civilFromInstant(now, timezone), -1);
      return dayStart(y.year, y.month, y.day, timezone);
    },
  },
  {
    label: 'Tomorrow',
    getValue: ({ timezone, now }) => {
      const t = addDays(civilFromInstant(now, timezone), 1);
      return dayStart(t.year, t.month, t.day, timezone);
    },
  },
];

const ranges: DatePickerPreset[] = [
  {
    label: 'Today',
    getValue: ({ timezone, now }) => {
      const t = civilFromInstant(now, timezone);
      return { start: dayStart(t.year, t.month, t.day, timezone), end: dayEnd(t.year, t.month, t.day, timezone) };
    },
  },
  {
    label: 'Yesterday',
    getValue: ({ timezone, now }) => {
      const y = addDays(civilFromInstant(now, timezone), -1);
      return { start: dayStart(y.year, y.month, y.day, timezone), end: dayEnd(y.year, y.month, y.day, timezone) };
    },
  },
  {
    label: 'This Week',
    getValue: ({ timezone, now }) => {
      // Sunday-started week (matches the calendar's default firstDayOfWeek), full
      // Sun–Sat span like "This Month" spans the whole month.
      const s = startOfWeek(civilFromInstant(now, timezone), 0);
      const e = addDays(s, 6);
      return { start: dayStart(s.year, s.month, s.day, timezone), end: dayEnd(e.year, e.month, e.day, timezone) };
    },
  },
  {
    label: 'Last Week',
    getValue: ({ timezone, now }) => {
      const s = addDays(startOfWeek(civilFromInstant(now, timezone), 0), -7);
      const e = addDays(s, 6);
      return { start: dayStart(s.year, s.month, s.day, timezone), end: dayEnd(e.year, e.month, e.day, timezone) };
    },
  },
  {
    label: 'Last 7 Days',
    getValue: ({ timezone, now }) => {
      const t = civilFromInstant(now, timezone);
      const s = addDays(t, -6);
      return { start: dayStart(s.year, s.month, s.day, timezone), end: dayEnd(t.year, t.month, t.day, timezone) };
    },
  },
  {
    label: 'Last 30 Days',
    getValue: ({ timezone, now }) => {
      const t = civilFromInstant(now, timezone);
      const s = addDays(t, -29);
      return { start: dayStart(s.year, s.month, s.day, timezone), end: dayEnd(t.year, t.month, t.day, timezone) };
    },
  },
  {
    label: 'This Month',
    getValue: ({ timezone, now }) => {
      const t = civilFromInstant(now, timezone);
      return {
        start: dayStart(t.year, t.month, 1, timezone),
        end: dayEnd(t.year, t.month, daysInMonth(t.year, t.month), timezone),
      };
    },
  },
  {
    label: 'Last Month',
    getValue: ({ timezone, now }) => {
      const t = addMonths(civilFromInstant(now, timezone), -1);
      return {
        start: dayStart(t.year, t.month, 1, timezone),
        end: dayEnd(t.year, t.month, daysInMonth(t.year, t.month), timezone),
      };
    },
  },
  {
    label: 'This Quarter',
    getValue: ({ timezone, now }) => {
      const t = civilFromInstant(now, timezone);
      const qs = quarterStartMonth(t.month);
      const qe = qs + 2;
      return { start: dayStart(t.year, qs, 1, timezone), end: dayEnd(t.year, qe, daysInMonth(t.year, qe), timezone) };
    },
  },
  {
    label: 'This Year',
    getValue: ({ timezone, now }) => {
      const t = civilFromInstant(now, timezone);
      return { start: dayStart(t.year, 1, 1, timezone), end: dayEnd(t.year, 12, 31, timezone) };
    },
  },
];

/** The default preset list appropriate for the given mode. */
export function defaultPresets(mode: DatePickerMode): DatePickerPreset[] {
  const isRange = mode === 'range' || mode === 'datetime-range';
  return isRange ? ranges : single;
}
